import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from "@angular/core";

import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { forkJoin, interval, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { ToastrService } from "ngx-toastr";

import { SupportTicket_uploadAttachment } from "src/app/GlobalVariable";
import { UserLoginService } from "src/app/login/userlogin.service";
import { UserAffiliationMappingService } from "src/app/housing-society/Interface & Service/userAffiliationMapping/userAffiliationMapping.service";
import { RaiseTicketService } from "../../raise-ticket-form/raise-ticket.service";

import {
  SupportTicketsReadAllPaginatedDTO,
  SupportTicketsReadAllPaginatedList,
  SupportTicketsReadAllPaginatedRequestDTO,
  SupportTicketsReadByIdRequestDTO,
  SupportTicketsUpdateRequestDTO,
  TicketActivityCreateRequestDTO,
} from "../../raise-ticket-form/raise-ticket.interface";

type TicketStatus =
  | "Open"
  | "Re-Open"
  | "In Progress"
  | "Resolved"
  | "Closed"
  | "Deleted";

type TicketModalMode =
  | "view"
  | "assign"
  | "status"
  | null;

interface TicketAttachment {
  path: string;
  name: string;
  isLatest: boolean;
}

interface TicketActivity {
  comment: string;
  createdBy: string;
  createdOn: string;
  senderType: "ADMIN" | "PATIENT" | "SYSTEM";
  isMessage: boolean;
  attachmentPath: string | null;
  attachmentName: string | null;
}

interface HospitalAdminTicket {
  ticketId: number;
  ticketNo: string;
  ticketNoForApi: string | null;

  moduleType: string;
  moduleId: number;

  ticketOwner: string;
  userId: number;

  name: string;
  email: string;
  mobile: string;

  department: string;
  category: string;
  subCategory: string;

  subject: string;
  description: string;

  status: TicketStatus;
  assignedTo: string | null;

  tagList: string | null;
  priority: string | null;
  affectsCustomer: string | null;
  appVersion: string | null;

  dueDate: string | null;
  estimatedDuration: string | null;
  actualDuration: string | null;
  targetDate: string | null;
  resolutionDate: string | null;

  addField3: string | null;
  addField4: string | null;
  addField5: string | null;

  otherDetail1: string | null;
  otherDetail2: string | null;
  otherDetail3: string | null;
  otherDetail4: string | null;
  otherDetail5: string | null;
  otherDetail6: string | null;

  createdOn: string;
  updatedOn: string;

  attachmentName: string | null;
  attachmentPath: string | null;
  attachments: TicketAttachment[];

  activities: TicketActivity[];
  hasUnreadMessage: boolean;
  latestMessageOn: string | null;
}

@Component({
  selector: "app-hospital-raise-ticket",
  templateUrl: "./hospital-raise-ticket.component.html",
  styleUrls: ["./hospital-raise-ticket.component.css"],
})
export class HospitalRaiseTicketComponent
  implements OnInit, OnChanges
{
  /*
   * Pass the current HospitalId from the parent.
   */
  @Input() moduleId: number | null = null;

  /*
   * Default value for this hospital component.
   */
  @Input() moduleType: string = "HOSPITAL";

  private raiseTicketService =
    inject(RaiseTicketService);

  private userLoginService =
    inject(UserLoginService);

  /*
   * Society ticket assignment uses the same existing Housing Society
   * UserAffiliationMapping API that already loads Society Admin and
   * Committee Person records in the Society Members screen.
   */
  private userAffiliationMappingService =
    inject(UserAffiliationMappingService);

  private destroyRef =
    inject(DestroyRef);

  private toastr =
    inject(ToastrService);

  tickets: HospitalAdminTicket[] = [];

  searchText: string = "";
  selectedStatus: string = "All";

  pageSize: number = 10;
  currentPage: number = 1;
  totalRecords: number = 0;

  /*
   * Load the complete hospital ticket set once, then apply search,
   * status filtering and visible-table pagination locally.
   *
   * Previously the API loaded only the current 10-row server page.
   * Therefore searching Priyansh Desai could find only one row even
   * though the patient owned tickets on other server pages.
   */
  private readonly adminTicketFetchSize: number = 5000;

  isLoading: boolean = false;
  errorMessage: string = "";

  openActionMenuId: number | null = null;

  selectedTicket: HospitalAdminTicket | null =
    null;

  modalMode: TicketModalMode = null;

  selectedAssignee: string = "";
  assignmentDescription: string = "";

  updatedStatus: TicketStatus | "" = "";
  statusComment: string = "";
  isUpdatingStatus: boolean = false;
  isAssigningTicket: boolean = false;

  adminReplyText: string = "";
  isSendingReply: boolean = false;

  adminReplyFile: File | null = null;
  adminReplyFilePreviewUrl: string | null = null;
  isUploadingReplyAttachment: boolean = false;

  /*
   * Main ticket attachment actions use:
   * GET /M/ST/ViewAttachment/{ticketId}
   */
  isTicketAttachmentLoading: boolean = false;
  ticketAttachmentAction: "view" | "download" | null = null;

  private readonly adminMessagePrefix = "[ADMIN_MESSAGE]";
  private readonly patientMessagePrefix = "[PATIENT_MESSAGE]";
  private readonly adminMessageJsonPrefix = "[ADMIN_MESSAGE_JSON]";
  private readonly patientMessageJsonPrefix = "[PATIENT_MESSAGE_JSON]";

  /*
   * Message synchronization:
   * - CustomEvent handles messages created in the same browser window.
   * - BroadcastChannel handles other tabs/windows on the same origin.
   * - localStorage handles browsers where BroadcastChannel is unavailable.
   * - polling keeps separate browser sessions/devices synchronized from API.
   */
  private readonly messageBroadcastChannelName =
    "support-ticket-message-channel";

  private readonly messageStorageKey =
    "support-ticket-message-created";

  /*
   * Receives patient-side ticket status changes such as ReOpen.
   */
  private readonly ticketUpdateStorageKey =
    "support-ticket-updated";

  private readonly messageIndicatorPollIntervalMs =
    5000;

  /*
   * IMPORTANT:
   * Keep the old polling code available, but disable it by default.
   * Ticket APIs now refresh only on initial load, manual refresh,
   * module changes, or a real ticket/message update event.
   */
  private readonly enableAutomaticMessagePolling:
    boolean = false;

  private ticketListRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private readonly ticketListRefreshIntervalMs = 15000;
  private hasLoadedTicketList = false;

  /*
   * Do not reload the complete ticket list whenever the browser
   * regains focus or the tab becomes visible.
   */
  private readonly refreshTicketsOnWindowFocus:
    boolean = false;

  private messageBroadcastChannel:
    BroadcastChannel | null = null;

  private isRefreshingMessageIndicators:
    boolean = false;

  /*
   * Keep the existing Assign Ticket dropdown flow, but resolve the
   * available assignees from the active ticket module.
   *
   * The same component is reused for Hospital, School and Society.
   * Previously this list was permanently hard-coded with hospital teams,
   * which is why the School and Society screens also showed hospital users.
   */
  private readonly assigneeListByModule:
    Record<string, string[]> = {
    HOSPITAL: [
      "Hospital Support Admin",
      "Billing Support Team",
      "Technical Support Team",
      "OPD Support Team",
    ],

    SCHOOL: [
      "School Support Admin",
      "Fees Support Team",
      "Admission Support Team",
      "Academic Support Team",
      "Examination Cell",
      "Class Teacher",
      "Technical Support Team",
    ],

    SOCIETY: [
      "Society Support Admin",
      "Maintenance Support Team",
      "Accounts Support Team",
      "Facility Support Team",
      "Security Support Team",
      "Technical Support Team",
    ],

    GENERAL: [
      "Support Admin",
      "Billing Support Team",
      "Technical Support Team",
    ],
  };

  assigneeList: string[] = [];

  ngOnInit(): void {
    this.updateAssigneeListForCurrentModule();
    this.initializeMessageSynchronization();
    this.startMessageIndicatorPolling();
    this.getHospitalTickets();
    this.startTicketListRefresh();
  }

  private startTicketListRefresh(): void {
    if (typeof window === "undefined" || this.ticketListRefreshTimer) {
      return;
    }

    this.ticketListRefreshTimer = setInterval(() => {
      if (
        document.visibilityState === "visible" &&
        !this.isLoading &&
        Number(this.moduleId || 0) > 0
      ) {
        this.getHospitalTickets();
      }
    }, this.ticketListRefreshIntervalMs);

    this.destroyRef.onDestroy(() => {
      if (this.ticketListRefreshTimer) {
        clearInterval(this.ticketListRefreshTimer);
        this.ticketListRefreshTimer = null;
      }
    });
  }

  ngOnChanges(
    changes: SimpleChanges,
  ): void {
    const moduleChanged =
      changes["moduleId"] ||
      changes["moduleType"];

    /*
     * ngOnInit handles the first API call.
     */
    if (
      moduleChanged &&
      !moduleChanged.firstChange
    ) {
      this.currentPage = 1;
      this.updateAssigneeListForCurrentModule();
      this.getHospitalTickets();
    }
  }

  /*
   * Resolve a stable module key without changing the value used by the
   * existing ticket APIs. This supports values such as:
   * HOSPITAL, SCHOOL, SOCIETY, HOUSING_SOCIETY and Housing Society.
   */
  private resolveAssigneeModuleKey(
    moduleValue?: string | null,
  ): string {
    const normalizedModule =
      String(
        moduleValue ||
        "",
      )
        .trim()
        .toUpperCase()
        .replace(
          /[^A-Z0-9]+/g,
          "",
        );

    if (
      normalizedModule.includes(
        "SCHOOL",
      )
    ) {
      return "SCHOOL";
    }

    if (
      normalizedModule.includes(
        "SOCIETY",
      ) ||
      normalizedModule.includes(
        "HOUSING",
      )
    ) {
      return "SOCIETY";
    }

    if (
      normalizedModule.includes(
        "HOSPITAL",
      )
    ) {
      return "HOSPITAL";
    }

    return "GENERAL";
  }

  /*
   * Use the selected ticket's module first because this is the exact record
   * being assigned. Fall back to the component input when the modal is not
   * open yet.
   */
  private updateAssigneeListForCurrentModule(
    ticket?: HospitalAdminTicket | null,
  ): void {
    const moduleKey =
      this.resolveAssigneeModuleKey(
        ticket?.moduleType ||
        ticket?.department ||
        this.selectedTicket?.moduleType ||
        this.selectedTicket?.department ||
        this.moduleType,
      );

    /*
     * MINIMUM SOCIETY FIX:
     * Do not use the hard-coded Society support-team list. Load the real
     * Society Admin + Committee Person designations for this Society from
     * the existing UserAffiliationMapping API. Hospital and School keep
     * their current flow exactly as before.
     */
    if (moduleKey === "SOCIETY") {
      this.loadSocietyAssigneeList(
        ticket,
      );

      return;
    }

    this.assigneeList = [
      ...(
        this.assigneeListByModule[
          moduleKey
        ] ||
        this.assigneeListByModule[
          "GENERAL"
        ]
      ),
    ];

    /*
     * Do not keep a hospital assignee selected when a School or Society
     * ticket is opened. Existing valid assignees for the same module remain
     * selected exactly as before.
     */
    if (
      this.selectedAssignee &&
      !this.assigneeList.includes(
        this.selectedAssignee,
      )
    ) {
      this.selectedAssignee = "";
    }
  }

  /*
   * Loads the same Society Admin / Committee Person records used by the
   * Housing Society Members component. Only the designation is used as the
   * assignment value so the existing TicketAssignee_Create and
   * SupportTickets_Update payloads do not change.
   *
   * Only records actually returned by the API are shown.
   */
  private loadSocietyAssigneeList(
    ticket?: HospitalAdminTicket | null,
  ): void {
    const societyId =
      Number(
        ticket?.moduleId ||
        this.selectedTicket?.moduleId ||
        this.moduleId ||
        0,
      );

    if (societyId <= 0) {
      this.assigneeList = [];
      this.selectedAssignee = "";
      return;
    }

    const basePayload = {
      societyId,
      pageNo: 1,
      pageSize: 9999,
      orderByClause: "",
      whereClause: "",
    };

    const societyAdminCall =
      this.userAffiliationMappingService
        .SmUserAffiliationMapping_ReadUserBySocietyId({
          ...basePayload,
          affiliationType: "SocietyAdmin",
        })
        .pipe(
          catchError(
            (error: any) => {
              console.error(
                "Unable to load Society Admin ticket assignees:",
                error,
              );

              return of({
                items: [],
              } as any);
            },
          ),
        );

    const committeeCall =
      this.userAffiliationMappingService
        .SmUserAffiliationMapping_ReadUserBySocietyId({
          ...basePayload,
          affiliationType: "CommitteePerson",
        })
        .pipe(
          catchError(
            (error: any) => {
              console.error(
                "Unable to load Committee Person ticket assignees:",
                error,
              );

              return of({
                items: [],
              } as any);
            },
          ),
        );

    forkJoin([
      societyAdminCall,
      committeeCall,
    ])
      .pipe(
        takeUntilDestroyed(
          this.destroyRef,
        ),
      )
      .subscribe({
        next: (
          responses: any[],
        ) => {
          const members = [
            ...(Array.isArray(
              responses?.[0]?.items,
            )
              ? responses[0].items
              : []),
            ...(Array.isArray(
              responses?.[1]?.items,
            )
              ? responses[1].items
              : []),
          ];

          const apiAssignees =
            members
              .map(
                (member: any) => {
                  const designation =
                    String(
                      member?.designation ??
                      member?.Designation ??
                      "",
                    ).trim();

                  const affiliationType =
                    String(
                      member?.affiliationType ??
                      member?.AffiliationType ??
                      "",
                    )
                      .trim()
                      .toUpperCase();

                  if (designation) {
                    return designation;
                  }

                  if (
                    affiliationType ===
                    "SOCIETYADMIN"
                  ) {
                    return "Society Admin";
                  }

                  return "";
                },
              )
              .filter(
                (value: string) =>
                  Boolean(value),
              );

          /*
           * Remove duplicate designations while keeping the API order.
           */
          this.assigneeList =
            Array.from(
              new Set(
                apiAssignees,
              ),
            );

          const currentAssignee =
            String(
              ticket?.assignedTo ||
              this.selectedTicket?.assignedTo ||
              "",
            ).trim();

          if (
            currentAssignee &&
            this.assigneeList.includes(
              currentAssignee,
            )
          ) {
            this.selectedAssignee =
              currentAssignee;
          } else if (
            this.selectedAssignee &&
            !this.assigneeList.includes(
              this.selectedAssignee,
            )
          ) {
            this.selectedAssignee = "";
          }
        },
      });
  }

  private initializeMessageSynchronization(): void {
    if (
      typeof window === "undefined"
    ) {
      return;
    }

    if (
      "BroadcastChannel" in window
    ) {
      try {
        this.messageBroadcastChannel =
          new BroadcastChannel(
            this.messageBroadcastChannelName,
          );

        this.messageBroadcastChannel.onmessage =
          (event: MessageEvent) => {
            this.handleSupportTicketMessageSignal(
              event?.data,
            );
          };
      } catch (error) {
        console.warn(
          "Support-ticket BroadcastChannel could not be initialized:",
          error,
        );

        this.messageBroadcastChannel =
          null;
      }
    }

    this.destroyRef.onDestroy(
      () => {
        try {
          this.messageBroadcastChannel?.close();
        } catch (error) {
          console.warn(
            "Support-ticket BroadcastChannel could not be closed:",
            error,
          );
        }

        this.messageBroadcastChannel =
          null;
      },
    );
  }

  private publishSupportTicketMessageSignal(
    signal: {
      ticketId: number;
      senderType: "ADMIN" | "PATIENT";
      createdOn?: string;
    },
  ): void {
    if (
      typeof window === "undefined"
    ) {
      return;
    }

    const messageSignal = {
      ...signal,
      createdOn:
        signal.createdOn ||
        new Date().toISOString(),
      signalId:
        `${Date.now()}-${Math.random()}`,
    };

    /*
     * Current Angular window/component.
     */
    window.dispatchEvent(
      new CustomEvent(
        "support-ticket-message-created",
        {
          detail:
            messageSignal,
        },
      ),
    );

    /*
     * Other same-origin tabs/windows.
     */
    try {
      this.messageBroadcastChannel?.postMessage(
        messageSignal,
      );
    } catch (error) {
      console.warn(
        "Unable to publish support-ticket message through BroadcastChannel:",
        error,
      );
    }

    /*
     * Fallback for browsers where BroadcastChannel is unavailable.
     */
    try {
      localStorage.setItem(
        this.messageStorageKey,
        JSON.stringify(
          messageSignal,
        ),
      );
    } catch (error) {
      console.warn(
        "Unable to publish support-ticket message through localStorage:",
        error,
      );
    }
  }

  private startMessageIndicatorPolling(): void {
    /*
     * The function and original polling flow are intentionally retained
     * so they can be re-enabled later without changing the component.
     * It is disabled now to stop ReadByTicketId calls every few seconds.
     */
    if (
      !this.enableAutomaticMessagePolling
    ) {
      return;
    }

    interval(
      this.messageIndicatorPollIntervalMs,
    )
      .pipe(
        takeUntilDestroyed(
          this.destroyRef,
        ),
      )
      .subscribe(
        () => {
          /*
           * CustomEvent/BroadcastChannel gives an immediate update.
           * Polling is the fallback for another browser, incognito window,
           * another device or any context that cannot share browser events.
           */
          if (
            document.hidden ||
            this.isLoading ||
            this.tickets.length === 0
          ) {
            return;
          }

          this.refreshAdminMessageIndicators();
        },
      );
  }

  private handleSupportTicketMessageSignal(
    signal: any,
  ): void {
    const ticketId =
      Number(
        signal?.ticketId ??
        signal?.TicketId ??
        0,
      );

    const senderType =
      String(
        signal?.senderType ??
        signal?.SenderType ??
        "",
      )
        .trim()
        .toUpperCase();

    if (
      ticketId <= 0 ||
      senderType !== "PATIENT"
    ) {
      return;
    }

    const ticket =
      this.tickets.find(
        (
          item:
            HospitalAdminTicket,
        ) =>
          item.ticketId ===
          ticketId,
      );

    const selectedTicketIsOpen =
      this.selectedTicket?.ticketId ===
      ticketId &&
      this.modalMode === "view";

    if (!ticket) {
      this.getHospitalTickets();
      return;
    }

    /*
     * Update only the affected ticket. Do not call ReadByTicketId for
     * every ticket in the grid.
     */
    if (
      ticket &&
      !selectedTicketIsOpen
    ) {
      ticket.hasUnreadMessage =
        true;

      ticket.latestMessageOn =
        String(
          signal?.createdOn ??
          signal?.CreatedOn ??
          new Date().toISOString(),
        );
    }

    /*
     * When the affected ticket is already open, refresh only that
     * conversation after the patient message has been saved.
     */
    if (
      selectedTicketIsOpen &&
      this.selectedTicket
    ) {
      this.markAdminTicketMessagesAsRead(
        this.selectedTicket,
      );

      this.loadTicketActivities(
        this.selectedTicket,
      );
    }
  }

  /**
   * Calls:
   * POST /ReadAllPaginated
   */
  getHospitalTickets(): void {
    const hospitalId =
      Number(this.moduleId || 0);

    if (hospitalId <= 0) {
      this.tickets = [];
      this.totalRecords = 0;

      this.errorMessage =
        "Hospital ID is unavailable.";

      return;
    }

    if (this.isLoading) {
      return;
    }

    const payload:
      SupportTicketsReadAllPaginatedRequestDTO = {
      pageSize: this.adminTicketFetchSize,
      pageNo: 1,

      moduleType: String(
        this.moduleType || "HOSPITAL",
      )
        .trim()
        .toUpperCase(),

      moduleId: hospitalId,

      /*
       * Keep these null for now.
       * Do not create raw SQL filters from UI values.
       */
      orderByClause: null,
      whereClause: null,
    };

;

    this.isLoading = true;
    this.errorMessage = "";
    this.openActionMenuId = null;

    this.raiseTicketService
      .SupportTickets_ReadAllPaginated(
        payload,
      )
      .subscribe({
        next: (
          response:
            SupportTicketsReadAllPaginatedList |
            any,
        ) => {

          const apiItems =
            this.extractTicketItems(
              response,
            );

          /*
           * ReOpen currently returns AssignedTo as null from the ticket list
           * in some environments. Preserve the assignee already visible in
           * memory before replacing the refreshed list.
           */
          const existingAssigneeByTicketId =
            new Map<number, string>();

          this.tickets.forEach(
            (
              existingTicket:
                HospitalAdminTicket,
            ) => {
              const existingAssignee =
                String(
                  existingTicket.assignedTo ||
                  "",
                ).trim();

              if (existingAssignee) {
                existingAssigneeByTicketId.set(
                  existingTicket.ticketId,
                  existingAssignee,
                );
              }
            },
          );

          const previousTicketsById = new Map(
            this.tickets.map((ticket: HospitalAdminTicket) => [ticket.ticketId, ticket]),
          );
          const existingTicketIds = new Set(previousTicketsById.keys());

          this.tickets =
            apiItems.map(
              (
                ticket:
                  SupportTicketsReadAllPaginatedDTO,
              ) => {
                const mappedTicket =
                  this.mapApiTicket(
                    ticket,
                  );

                if (
                  !mappedTicket.assignedTo
                ) {
                  mappedTicket.assignedTo =
                    existingAssigneeByTicketId.get(
                      mappedTicket.ticketId,
                    ) ||
                    null;
                }

                const previousTicket = previousTicketsById.get(mappedTicket.ticketId);
                if (previousTicket) {
                  mappedTicket.hasUnreadMessage = previousTicket.hasUnreadMessage;
                  mappedTicket.latestMessageOn = previousTicket.latestMessageOn;
                } else if (this.hasLoadedTicketList && !existingTicketIds.has(mappedTicket.ticketId)) {
                  // A ticket created after the initial list load needs admin attention.
                  mappedTicket.hasUnreadMessage = true;
                  mappedTicket.latestMessageOn = mappedTicket.createdOn;
                }

                return mappedTicket;
              },
            );
          this.hasLoadedTicketList = true;

          /*
           * Also restore the latest assignee from TicketAssignee history.
           * This keeps the assignee visible even after a full page reload
           * when the ReOpen API has cleared SupportTickets.AssignedTo.
           */
          this.hydrateMissingTicketAssignees();

          this.totalRecords =
            this.extractTotalCount(
              response,
              apiItems,
            );

          /*
           * Because all records are loaded into this.tickets, the visible
           * page is now local UI pagination. Keep the current page valid
           * after refreshes, deletions or a changed result count.
           */
          const availablePages =
            Math.max(
              1,
              Math.ceil(
                this.tickets.length /
                  this.pageSize,
              ),
            );

          if (
            this.currentPage >
            availablePages
          ) {
            this.currentPage =
              availablePages;
          }

          this.isLoading = false;
          if (previousTicketsById.size === 0) {
            this.refreshAdminMessageIndicators();
          }
        },

        error: (error: any) => {
          console.error(
            "SupportTickets_ReadAllPaginated error:",
            error,
          );

          this.tickets = [];
          this.totalRecords = 0;
          this.isLoading = false;

          this.errorMessage =
            error?.error?.message ||
            error?.message ||
            "Unable to load hospital support tickets.";
        },
      });
  }

  private extractTicketItems(
    response: any,
  ): SupportTicketsReadAllPaginatedDTO[] {
    if (
      Array.isArray(response?.items)
    ) {
      return response.items;
    }

    if (
      Array.isArray(
        response?.data?.items,
      )
    ) {
      return response.data.items;
    }

    if (
      Array.isArray(
        response?.result?.items,
      )
    ) {
      return response.result.items;
    }

    /*
     * Some APIs return the list directly.
     */
    if (Array.isArray(response)) {
      return response;
    }

    return [];
  }

  private extractTotalCount(
    response: any,
    items:
      SupportTicketsReadAllPaginatedDTO[],
  ): number {
    const responseTotal = Number(
      response?.totalCount ??
      response?.data?.totalCount ??
      response?.result?.totalCount ??
      0,
    );

    if (responseTotal > 0) {
      return responseTotal;
    }

    const itemTotal = Number(
      items[0]?.totalCount || 0,
    );

    if (itemTotal > 0) {
      return itemTotal;
    }

    return items.length;
  }

  private mapApiTicket(
    ticket:
      SupportTicketsReadAllPaginatedDTO,
  ): HospitalAdminTicket {
    const apiTicket =
      ticket as any;

    const ticketOwner =
      String(
        apiTicket.ticketOwner ??
        apiTicket.TicketOwner ??
        "",
      ).trim();

    const attachmentPath =
      apiTicket.attachmentPath ??
      apiTicket.AttachmentPath ??
      null;

    const addField3 =
      apiTicket.addField3 ??
      apiTicket.AddField3 ??
      null;

    const attachments =
      this.extractTicketAttachments(
        apiTicket,
      );

    return {
      ticketId:
        Number(
          apiTicket.ticketId ??
          apiTicket.TicketId ??
          0,
        ),

      ticketNo:
        apiTicket.ticketNo ||
        apiTicket.TicketNo ||
        `Ticket #${
          apiTicket.ticketId ??
          apiTicket.TicketId ??
          ""
        }`,

      ticketNoForApi:
        apiTicket.ticketNo ||
        apiTicket.TicketNo ||
        null,

      moduleType:
        String(
          apiTicket.moduleType ||
          apiTicket.ModuleType ||
          this.moduleType ||
          "HOSPITAL",
        )
          .trim()
          .toUpperCase(),

      moduleId:
        Number(
          apiTicket.moduleId ||
          apiTicket.ModuleId ||
          this.moduleId ||
          0,
        ),

      ticketOwner,

      userId:
        Number(ticketOwner || 0),

      name:
        apiTicket.raisedBy ||
        apiTicket.RaisedBy ||
        ticketOwner ||
        "--",

      email:
        apiTicket.otherDetail5 ||
        apiTicket.OtherDetail5 ||
        "--",

      mobile:
        apiTicket.otherDetail6 ||
        apiTicket.OtherDetail6 ||
        "--",

      department:
        apiTicket.department ||
        apiTicket.Department ||
        apiTicket.moduleType ||
        apiTicket.ModuleType ||
        "Hospital",

      category:
        apiTicket.category ||
        apiTicket.Category ||
        "--",

      subCategory:
        apiTicket.tType ||
        apiTicket.TType ||
        "--",

      subject:
        apiTicket.title ||
        apiTicket.Title ||
        "--",

      description:
        apiTicket.tDesc ||
        apiTicket.TDesc ||
        "--",

      status:
        this.normalizeTicketStatus(
          apiTicket.ticketStatus ??
          apiTicket.TicketStatus ??
          null,
        ),

      assignedTo:
        apiTicket.assignedTo ||
        apiTicket.AssignedTo ||
        null,

      tagList:
        apiTicket.tagList ||
        apiTicket.TagList ||
        null,

      priority:
        apiTicket.tPriority ||
        apiTicket.TPriority ||
        null,

      affectsCustomer:
        apiTicket.affectsCustomer ||
        apiTicket.AffectsCustomer ||
        null,

      appVersion:
        apiTicket.appVersion ||
        apiTicket.AppVersion ||
        null,

      dueDate:
        apiTicket.dueDate ||
        apiTicket.DueDate ||
        null,

      estimatedDuration:
        apiTicket.estimatedDuration ||
        apiTicket.EstimatedDuration ||
        null,

      actualDuration:
        apiTicket.actualDuration ||
        apiTicket.ActualDuration ||
        null,

      targetDate:
        apiTicket.targetDate ||
        apiTicket.TargetDate ||
        null,

      resolutionDate:
        apiTicket.resolutionDate ||
        apiTicket.ResolutionDate ||
        null,

      addField3,

      addField4:
        apiTicket.addField4 ||
        apiTicket.AddField4 ||
        null,

      addField5:
        apiTicket.addField5 ||
        apiTicket.AddField5 ||
        null,

      otherDetail1:
        apiTicket.otherDetail1 ||
        apiTicket.OtherDetail1 ||
        null,

      otherDetail2:
        apiTicket.otherDetail2 ||
        apiTicket.OtherDetail2 ||
        null,

      otherDetail3:
        apiTicket.otherDetail3 ||
        apiTicket.OtherDetail3 ||
        null,

      otherDetail4:
        apiTicket.otherDetail4 ||
        apiTicket.OtherDetail4 ||
        null,

      otherDetail5:
        apiTicket.otherDetail5 ||
        apiTicket.OtherDetail5 ||
        null,

      otherDetail6:
        apiTicket.otherDetail6 ||
        apiTicket.OtherDetail6 ||
        null,

      createdOn:
        apiTicket.createdOn ||
        apiTicket.CreatedOn ||
        "",

      updatedOn:
        apiTicket.modifiedOn ||
        apiTicket.ModifiedOn ||
        apiTicket.createdOn ||
        apiTicket.CreatedOn ||
        "",

      attachmentName:
        this.getAttachmentName(
          attachmentPath,
        ),

      attachmentPath,
      attachments,

      activities: [],
      hasUnreadMessage: false,
      latestMessageOn: null,
    };
  }

  private normalizeTicketStatus(
    status: string | null,
  ): TicketStatus {
    const normalized =
      String(status || "")
        .trim()
        .toLowerCase()
        .replace(/[_-]+/g, " ");

    switch (normalized) {
      case "in progress":
      case "inprogress":
      case "processing":
      case "assigned":
        return "In Progress";

      case "resolved":
      case "completed":
        return "Resolved";

      case "close":
      case "closed":
      case "force close":
      case "force closed":
        return "Closed";

      case "reopen":
      case "re open":
      case "reopened":
      case "re opened":
        return "Re-Open";

      case "delete":
      case "deleted":
      case "deleted by user":
      case "user deleted":
        return "Deleted";

      case "open":
      case "new":
      default:
        return "Open";
    }
  }

  private getAttachmentName(
    attachmentPath: string | null,
  ): string | null {
    if (!attachmentPath) {
      return null;
    }

    const normalizedPath =
      attachmentPath.replace(
        /\\/g,
        "/",
      );

    try {
      const parsedUrl =
        new URL(
          normalizedPath,
          window.location.origin,
        );

      return (
        decodeURIComponent(
          parsedUrl.pathname
            .split("/")
            .filter(Boolean)
            .pop() ||
          "",
        ) ||
        attachmentPath
      );
    } catch {
      return (
        decodeURIComponent(
          normalizedPath
            .split("?")[0]
            .split("#")[0]
            .split("/")
            .filter(Boolean)
            .pop() ||
          "",
        ) ||
        attachmentPath
      );
    }
  }

  private extractTicketAttachments(
    ticket: any,
  ): TicketAttachment[] {
    const latestPath =
      String(
        ticket?.attachmentPath ??
        ticket?.AttachmentPath ??
        "",
      ).trim();

    const storedHistory =
      ticket?.addField3 ??
      ticket?.AddField3 ??
      null;

    const candidatePaths:
      string[] = [];

    const addCandidate = (
      value: any,
    ): void => {
      const path =
        String(
          value ??
          "",
        ).trim();

      if (path) {
        candidatePaths.push(
          path,
        );
      }
    };

    if (
      Array.isArray(
        storedHistory,
      )
    ) {
      storedHistory.forEach(
        addCandidate,
      );
    } else if (
      storedHistory !== null &&
      storedHistory !== undefined &&
      String(
        storedHistory,
      ).trim()
    ) {
      const historyText =
        String(
          storedHistory,
        ).trim();

      try {
        const parsedHistory =
          JSON.parse(
            historyText,
          );

        if (
          Array.isArray(
            parsedHistory,
          )
        ) {
          parsedHistory.forEach(
            addCandidate,
          );
        } else {
          addCandidate(
            parsedHistory,
          );
        }
      } catch {
        /*
         * Keep old rows working when AddField3 contains one plain path.
         */
        addCandidate(
          historyText,
        );
      }
    }

    addCandidate(
      latestPath,
    );

    const uniquePaths =
      new Map<
        string,
        string
      >();

    candidatePaths.forEach(
      (
        path: string,
      ) => {
        const normalizedPath =
          this.normalizeAttachmentPathForApi(
            path,
          );

        if (
          normalizedPath &&
          !uniquePaths.has(
            normalizedPath.toLowerCase(),
          )
        ) {
          uniquePaths.set(
            normalizedPath.toLowerCase(),
            normalizedPath,
          );
        }
      },
    );

    const normalizedLatestPath =
      this.normalizeAttachmentPathForApi(
        latestPath,
      );

    return Array.from(
      uniquePaths.values(),
    ).map(
      (
        path: string,
      ) => ({
        path,
        name:
          this.getAttachmentName(
            path,
          ) ||
          "Ticket attachment",
        isLatest:
          Boolean(
            normalizedLatestPath &&
            path.toLowerCase() ===
              normalizedLatestPath.toLowerCase(),
          ),
      }),
    );
  }

  private normalizeAttachmentPathForApi(
    attachmentPath: string,
  ): string {
    const value =
      String(
        attachmentPath ||
        "",
      )
        .trim()
        .replace(
          /\\/g,
          "/",
        );

    if (!value) {
      return "";
    }

    try {
      if (
        /^https?:/i.test(
          value,
        )
      ) {
        return decodeURIComponent(
          new URL(
            value,
          ).pathname,
        ).replace(
          /^\/+/, 
          "",
        );
      }
    } catch {
      /*
       * Fall through and use the relative path.
       */
    }

    return value
      .split("?")[0]
      .split("#")[0]
      .replace(
        /^\/+/, 
        "",
      );
  }

  /*
   * ReOpen should not remove the previously assigned support team.
   *
   * The preferred permanent backend behaviour is to preserve AssignedTo in
   * SupportTickets_ReOpen. Until that backend change is available, restore
   * the latest assignment from TicketAssignee_ReadByTicketId whenever the
   * ticket list returns AssignedTo as null.
   */
  private hydrateMissingTicketAssignees(): void {
    const ticketsWithoutAssignee =
      this.tickets.filter(
        (
          ticket:
            HospitalAdminTicket,
        ) =>
          ticket.ticketId > 0 &&
          !String(
            ticket.assignedTo ||
            "",
          ).trim(),
      );

    ticketsWithoutAssignee.forEach(
      (
        ticket:
          HospitalAdminTicket,
      ) => {
        this.raiseTicketService
          .TicketAssignee_ReadByTicketId({
            ticketId:
              ticket.ticketId,
          })
          .pipe(
            takeUntilDestroyed(
              this.destroyRef,
            ),
          )
          .subscribe({
            next: (
              response: any,
            ) => {
              const assignee =
                this.extractLatestAssignee(
                  response,
                );

              if (!assignee) {
                return;
              }

              /*
               * Update the current object and the corresponding list object.
               * This keeps Angular's existing table flow unchanged.
               */
              ticket.assignedTo =
                assignee;

              const currentTicket =
                this.tickets.find(
                  (
                    item:
                      HospitalAdminTicket,
                  ) =>
                    item.ticketId ===
                    ticket.ticketId,
                );

              if (currentTicket) {
                currentTicket.assignedTo =
                  assignee;
              }

              if (
                this.selectedTicket?.ticketId ===
                ticket.ticketId
              ) {
                this.selectedTicket.assignedTo =
                  assignee;
              }
            },

            error: (
              error: any,
            ) => {
              console.error(
                `Unable to restore assignee for reopened ticket ${ticket.ticketId}:`,
                error,
              );
            },
          });
      },
    );
  }

  private extractLatestAssignee(
    response: any,
  ): string | null {
    const items =
      this.extractAssigneeItems(
        response,
      );

    if (
      items.length === 0
    ) {
      return null;
    }

    const sortedItems =
      [...items].sort(
        (
          first: any,
          second: any,
        ) => {
          const firstDate =
            new Date(
              first?.modifiedOn ??
              first?.ModifiedOn ??
              first?.createdOn ??
              first?.CreatedOn ??
              0,
            ).getTime();

          const secondDate =
            new Date(
              second?.modifiedOn ??
              second?.ModifiedOn ??
              second?.createdOn ??
              second?.CreatedOn ??
              0,
            ).getTime();

          if (
            firstDate !== secondDate
          ) {
            return (
              secondDate -
              firstDate
            );
          }

          return (
            Number(
              second?.ticketAssigneeId ??
              second?.TicketAssigneeId ??
              second?.assigneeId ??
              second?.AssigneeId ??
              second?.mappingId ??
              second?.MappingId ??
              0,
            ) -
            Number(
              first?.ticketAssigneeId ??
              first?.TicketAssigneeId ??
              first?.assigneeId ??
              first?.AssigneeId ??
              first?.mappingId ??
              first?.MappingId ??
              0,
            )
          );
        },
      );

    for (
      const item
      of sortedItems
    ) {
      const assignee =
        String(
          item?.assignedToName ??
          item?.AssignedToName ??
          item?.assigneeName ??
          item?.AssigneeName ??
          item?.assignedTo ??
          item?.AssignedTo ??
          "",
        ).trim();

      if (assignee) {
        return assignee;
      }
    }

    return null;
  }

  private extractAssigneeItems(
    response: any,
  ): any[] {
    if (
      Array.isArray(response)
    ) {
      return response;
    }

    if (
      Array.isArray(
        response?.items,
      )
    ) {
      return response.items;
    }

    if (
      Array.isArray(
        response?.data?.items,
      )
    ) {
      return response.data.items;
    }

    if (
      Array.isArray(
        response?.data,
      )
    ) {
      return response.data;
    }

    if (
      Array.isArray(
        response?.result?.items,
      )
    ) {
      return response.result.items;
    }

    if (
      Array.isArray(
        response?.result,
      )
    ) {
      return response.result;
    }

    if (
      response?.assignedTo ||
      response?.AssignedTo ||
      response?.assignedToName ||
      response?.AssignedToName
    ) {
      return [response];
    }

    return [];
  }

  /**
   * Search and dropdown filters currently
   * filter records from the loaded page.
   */
get filteredTickets():
  HospitalAdminTicket[] {
  const search =
    this.searchText
      .trim()
      .toLowerCase();

  return this.tickets.filter(
    (
      ticket:
        HospitalAdminTicket,
    ) => {
      const matchesSearch =
        !search ||
        ticket.ticketNo
          .toLowerCase()
          .includes(search) ||
        ticket.name
          .toLowerCase()
          .includes(search) ||
        ticket.subject
          .toLowerCase()
          .includes(search) ||
        ticket.category
          .toLowerCase()
          .includes(search);

      const matchesStatus =
        this.selectedStatus ===
          "All" ||
        ticket.status ===
          this.selectedStatus ||
        (
          this.selectedStatus ===
            "Open" &&
          ticket.status ===
            "Re-Open"
        );

      return (
        matchesSearch &&
        matchesStatus
      );
    },
  );
}

  /**
   * The backend has already paginated
   * the records, so do not slice again.
   */
  get paginatedTickets():
    HospitalAdminTicket[] {
    const startIndex =
      (this.currentPage - 1) *
      this.pageSize;

    return this.filteredTickets.slice(
      startIndex,
      startIndex +
        this.pageSize,
    );
  }

  get hasActiveFilter(): boolean {
    return (
      Boolean(
        this.searchText.trim(),
      ) ||
      this.selectedStatus !== "All"
    );
  }

  get totalPages(): number {
    return Math.max(
      1,
      Math.ceil(
        this.filteredTickets.length /
          this.pageSize,
      ),
    );
  }

  get pageNumbers(): number[] {
    return Array.from(
      {
        length: this.totalPages,
      },
      (
        _value: unknown,
        index: number,
      ) => index + 1,
    );
  }

  get pageStart(): number {
    if (
      this.filteredTickets.length ===
      0
    ) {
      return 0;
    }

    return (
      (this.currentPage - 1) *
        this.pageSize +
      1
    );
  }

  get pageEnd(): number {
    return Math.min(
      this.currentPage *
        this.pageSize,
      this.filteredTickets.length,
    );
  }

  getSerialNumber(
    index: number,
  ): number {
    return (
      (this.currentPage - 1) *
        this.pageSize +
      index +
      1
    );
  }

  /**
   * Dashboard counts represent all hospital tickets loaded from the API.
   *
   * The Resolved summary card represents all completed tickets.
   * Therefore both Resolved and Closed tickets are included in it.
   */
  getTicketCount(
    status: TicketStatus,
  ): number {
    if (status === "Resolved") {
      return this.tickets.filter(
        (
          ticket:
            HospitalAdminTicket,
        ) =>
          ticket.status === "Resolved" ||
          ticket.status === "Closed",
      ).length;
    }

    if (status === "Open") {
      return this.tickets.filter(
        (
          ticket:
            HospitalAdminTicket,
        ) =>
          ticket.status === "Open" ||
          ticket.status === "Re-Open",
      ).length;
    }

    return this.tickets.filter(
      (
        ticket:
          HospitalAdminTicket,
      ) =>
        ticket.status === status,
    ).length;
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.openActionMenuId = null;
  }

  changePageSize(
    pageSize: number | string,
  ): void {
    const size = Number(pageSize);

    if (
      !Number.isFinite(size) ||
      size <= 0
    ) {
      return;
    }

    this.pageSize = size;
    this.currentPage = 1;
    this.openActionMenuId = null;
  }

  goToPage(page: number): void {
    if (
      this.isLoading ||
      page < 1 ||
      page > this.totalPages ||
      page === this.currentPage
    ) {
      return;
    }

    this.currentPage = page;
    this.openActionMenuId = null;
  }

  refreshTickets(): void {
    this.currentPage = 1;
    this.getHospitalTickets();
  }

  toggleActionMenu(
    ticketId: number,
    event: MouseEvent,
  ): void {
    event.stopPropagation();

    this.openActionMenuId =
      this.openActionMenuId ===
      ticketId
        ? null
        : ticketId;
  }

  openTicketDetails(
    ticket: HospitalAdminTicket,
  ): void {
    this.selectedTicket = ticket;
    this.modalMode = "view";
    this.openActionMenuId = null;

    this.markAdminTicketMessagesAsRead(ticket);
    this.loadTicketActivities(ticket);
    this.loadTicketDetails(ticket);
  }

  private loadTicketDetails(
    ticket: HospitalAdminTicket,
  ): void {
    const payload:
      SupportTicketsReadByIdRequestDTO = {
      ticketId:
        ticket.ticketId,
    };

    this.raiseTicketService
      .SupportTickets_ReadById(
        payload,
      )
      .pipe(
        takeUntilDestroyed(
          this.destroyRef,
        ),
      )
      .subscribe({
        next: (
          response: any,
        ) => {
          const detail =
            response?.data ??
            response?.result ??
            response?.items?.[0] ??
            response;

          if (!detail) {
            return;
          }

          ticket.email =
            detail?.email ??
            detail?.Email ??
            detail?.emailId ??
            detail?.EmailId ??
            detail?.otherDetail5 ??
            detail?.OtherDetail5 ??
            ticket.email ??
            "--";

          ticket.mobile =
            detail?.mobile ??
            detail?.Mobile ??
            detail?.mobileNo ??
            detail?.MobileNo ??
            detail?.otherDetail6 ??
            detail?.OtherDetail6 ??
            ticket.mobile ??
            "--";

          ticket.assignedTo =
            detail?.assignedTo ??
            detail?.AssignedTo ??
            ticket.assignedTo;

          ticket.status =
            this.normalizeTicketStatus(
              detail?.ticketStatus ??
              detail?.TicketStatus ??
              ticket.status,
            );

          ticket.attachmentPath =
            detail?.attachmentPath ??
            detail?.AttachmentPath ??
            ticket.attachmentPath;

          ticket.addField3 =
            detail?.addField3 ??
            detail?.AddField3 ??
            ticket.addField3;

          ticket.attachmentName =
            this.getAttachmentName(
              ticket.attachmentPath,
            );

          ticket.attachments =
            this.extractTicketAttachments({
              ...detail,
              attachmentPath:
                ticket.attachmentPath,
              addField3:
                ticket.addField3,
            });
        },

        error: (
          error: any,
        ) => {
          console.error(
            "SupportTickets_ReadById admin detail error:",
            error,
          );
        },
      });
  }

  openAssignModal(
    ticket: HospitalAdminTicket,
  ): void {
    if (
      ticket.status === "Closed" ||
      ticket.status === "Deleted"
    ) {
      return;
    }

    this.selectedTicket = ticket;

    /*
     * Rebuild the dropdown from the ticket's actual module before patching
     * the current assignee. This prevents School/Society tickets from
     * showing the hospital support-team list.
     */
    this.updateAssigneeListForCurrentModule(
      ticket,
    );

    const currentAssignee =
      String(
        ticket.assignedTo ||
        "",
      ).trim();

    this.selectedAssignee =
      currentAssignee &&
      this.assigneeList.includes(
        currentAssignee,
      )
        ? currentAssignee
        : "";

    this.assignmentDescription = "";

    this.modalMode = "assign";
    this.openActionMenuId = null;
  }

  openStatusModal(
    ticket: HospitalAdminTicket,
  ): void {
    if (
      ticket.status === "Closed" ||
      ticket.status === "Deleted"
    ) {
      return;
    }

    this.selectedTicket = ticket;

    /*
     * Re-Open is a display status. The existing Update Status dropdown
     * continues to use Open, In Progress, Resolved and Closed.
     */
    this.updatedStatus =
      ticket.status ===
        "Re-Open"
        ? "Open"
        : ticket.status;

    this.statusComment = "";

    this.modalMode = "status";
    this.openActionMenuId = null;
  }

  assignTicket(): void {
    if (
      !this.selectedTicket ||
      !this.selectedAssignee ||
      this.isAssigningTicket
    ) {
      return;
    }

    const loggedInUser = this.userLoginService.User();
    const actionUser = String(loggedInUser?.userId || "").trim();

    if (!actionUser) {
      window.alert("Logged-in user information is unavailable.");
      return;
    }

    const ticket = this.selectedTicket;
    const assignmentComment =
      this.assignmentDescription.trim() ||
      `Ticket assigned to ${this.selectedAssignee}.`;

    this.isAssigningTicket = true;

    this.raiseTicketService
      .TicketAssignee_Create({
        ticketId: ticket.ticketId,
        assignedTo: this.selectedAssignee,
        workRatio: 100,
        assignDesc: assignmentComment,
        aStatus: "Active",
        actionUser,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const updatePayload =
            this.buildTicketUpdatePayload(
              ticket,
              "In Progress",
              actionUser,
              this.selectedAssignee,
            );

          this.raiseTicketService
            .SupportTickets_Update(updatePayload)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.raiseTicketService
                  .TicketActivity_Create({
                    ticketId: ticket.ticketId,
                    ticketComments: assignmentComment,
                    actionUser,
                  })
                  .pipe(takeUntilDestroyed(this.destroyRef))
                  .subscribe({
                    next: () => this.completeAssignment(),
                    error: (error: any) => {
                      console.error(
                        "TicketActivity_Create assignment error:",
                        error,
                      );
                      this.completeAssignment();
                    },
                  });
              },
              error: (error: any) => {
                this.isAssigningTicket = false;
                console.error(
                  "SupportTickets_Update assignment error:",
                  error,
                );
                window.alert(
                  error?.error?.message ||
                  error?.message ||
                  "Ticket assignee was saved, but ticket status could not be updated.",
                );
              },
            });
        },
        error: (error: any) => {
          this.isAssigningTicket = false;
          console.error("TicketAssignee_Create error:", error);
          window.alert(
            error?.error?.message ||
            error?.message ||
            "Unable to assign the ticket.",
          );
        },
      });
  }

  private completeAssignment(): void {
    this.isAssigningTicket = false;

    window.dispatchEvent(
      new CustomEvent(
        "support-ticket-updated",
      ),
    );

    this.toastr.success(
      "Ticket assigned successfully.",
      "Success",
    );

    this.closeModal();
    this.getHospitalTickets();
  }

  private loadTicketActivities(
    ticket: HospitalAdminTicket,
  ): void {
    this.raiseTicketService
      .TicketActivity_ReadByTicketId({
        ticketId: ticket.ticketId,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const items = this.extractActivityItems(response);

          ticket.activities = items.map((activity: any) =>
            this.mapTicketActivity(activity),
          );

          this.updateAdminUnreadState(ticket, ticket.activities);
        },
        error: (error: any) => {
          console.error(
            "TicketActivity_ReadByTicketId admin error:",
            error,
          );
          ticket.activities = [];
        },
      });
  }

  private extractActivityItems(response: any): any[] {
    if (Array.isArray(response?.items)) {
      return response.items;
    }

    if (Array.isArray(response?.data?.items)) {
      return response.data.items;
    }

    if (Array.isArray(response?.result?.items)) {
      return response.result.items;
    }

    if (Array.isArray(response)) {
      return response;
    }

    return [];
  }

  private mapTicketActivity(activity: any): TicketActivity {
    const rawComment = String(
      activity?.ticketComments ??
      activity?.TicketComments ??
      "",
    );

    let senderType: "ADMIN" | "PATIENT" | "SYSTEM" = "SYSTEM";
    let comment = rawComment;
    let attachmentPath: string | null = null;
    let attachmentName: string | null = null;

    const parseMessageJson = (
      value: string,
      prefix: string,
      type: "ADMIN" | "PATIENT",
    ): boolean => {
      if (!value.startsWith(prefix)) {
        return false;
      }

      senderType = type;

      try {
        const payload = JSON.parse(
          value.substring(prefix.length).trim(),
        );

        comment = String(payload?.message ?? "").trim();
        attachmentPath =
          String(payload?.attachmentPath ?? "").trim() || null;
        attachmentName =
          String(payload?.attachmentName ?? "").trim() ||
          this.getAttachmentName(attachmentPath);
      } catch (error) {
        console.error("Unable to parse ticket message payload:", error);
        comment = value.substring(prefix.length).trim();
      }

      return true;
    };

    if (
      !parseMessageJson(
        rawComment,
        this.adminMessageJsonPrefix,
        "ADMIN",
      ) &&
      !parseMessageJson(
        rawComment,
        this.patientMessageJsonPrefix,
        "PATIENT",
      )
    ) {
      if (rawComment.startsWith(this.adminMessagePrefix)) {
        senderType = "ADMIN";
        comment = rawComment
          .substring(this.adminMessagePrefix.length)
          .trim();
      } else if (rawComment.startsWith(this.patientMessagePrefix)) {
        senderType = "PATIENT";
        comment = rawComment
          .substring(this.patientMessagePrefix.length)
          .trim();
      }
    }

    return {
      comment,
      createdBy:
        activity?.createdBy ??
        activity?.CreatedBy ??
        (senderType === "PATIENT" ? "Patient" : "Support Team"),
      createdOn:
        activity?.createdOn ??
        activity?.CreatedOn ??
        "",
      senderType,
      isMessage: senderType !== "SYSTEM",
      attachmentPath,
      attachmentName,
    };
  }

  private refreshAdminMessageIndicators(): void {
    if (
      this.isRefreshingMessageIndicators ||
      this.tickets.length === 0
    ) {
      return;
    }

    this.isRefreshingMessageIndicators =
      true;

    let pendingRequests =
      this.tickets.length;

    const completeRequest =
      (): void => {
        pendingRequests -= 1;

        if (
          pendingRequests <= 0
        ) {
          this.isRefreshingMessageIndicators =
            false;
        }
      };

    this.tickets.forEach(
      (
        ticket:
          HospitalAdminTicket,
      ) => {
        this.raiseTicketService
          .TicketActivity_ReadByTicketId({
            ticketId:
              ticket.ticketId,
          })
          .pipe(
            takeUntilDestroyed(
              this.destroyRef,
            ),
          )
          .subscribe({
            next: (
              response: any,
            ) => {
              const activities =
                this.extractActivityItems(
                  response,
                ).map(
                  (
                    activity: any,
                  ) =>
                    this.mapTicketActivity(
                      activity,
                    ),
                );

              this.updateAdminUnreadState(
                ticket,
                activities,
              );
            },

            error: (
              error: any,
            ) => {
              console.error(
                `Unable to load message indicator for ticket ${ticket.ticketId}:`,
                error,
              );

              completeRequest();
            },

            complete:
              completeRequest,
          });
      },
    );
  }

  private updateAdminUnreadState(
    ticket: HospitalAdminTicket,
    activities: TicketActivity[],
  ): void {
    const patientMessages = activities
      .filter((activity: TicketActivity) =>
        activity.senderType === "PATIENT",
      )
      .sort(
        (first: TicketActivity, second: TicketActivity) =>
          new Date(second.createdOn || 0).getTime() -
          new Date(first.createdOn || 0).getTime(),
      );

    const latestPatientMessage = patientMessages[0] || null;

    ticket.latestMessageOn =
      latestPatientMessage?.createdOn || null;

    const lastReadOn = this.getMessageReadTime(
      "ADMIN",
      ticket.ticketId,
    );

    ticket.hasUnreadMessage = Boolean(
      latestPatientMessage &&
      new Date(latestPatientMessage.createdOn || 0).getTime() >
        lastReadOn,
    );
  }

  private markAdminTicketMessagesAsRead(
    ticket: HospitalAdminTicket,
  ): void {
    this.setMessageReadTime(
      "ADMIN",
      ticket.ticketId,
      Date.now(),
    );

    ticket.hasUnreadMessage = false;
  }

  private getMessageReadTime(
    viewer: "ADMIN" | "PATIENT",
    ticketId: number,
  ): number {
    const value = Number(
      localStorage.getItem(
        this.getMessageReadKey(viewer, ticketId),
      ) || 0,
    );

    return Number.isFinite(value) ? value : 0;
  }

  private setMessageReadTime(
    viewer: "ADMIN" | "PATIENT",
    ticketId: number,
    value: number,
  ): void {
    localStorage.setItem(
      this.getMessageReadKey(viewer, ticketId),
      String(value),
    );
  }

  private getMessageReadKey(
    viewer: "ADMIN" | "PATIENT",
    ticketId: number,
  ): string {
    const loggedInUser = this.userLoginService.User() as any;
    const userId =
      loggedInUser?.userId ??
      loggedInUser?.UserId ??
      "0";

    return `support-ticket-message-read:${viewer}:${userId}:${ticketId}`;
  }

  onAdminReplyFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;

    if (!file) {
      this.clearAdminReplyFile(input);
      return;
    }

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "application/pdf",
    ];

    const allowedExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".pdf",
    ];

    const fileName = file.name.toLowerCase();
    const extensionAllowed = allowedExtensions.some(
      (extension: string) => fileName.endsWith(extension),
    );

    if (
      !allowedTypes.includes(file.type) &&
      !extensionAllowed
    ) {
      window.alert("Only JPG, JPEG, PNG and PDF files are allowed.");
      this.clearAdminReplyFile(input);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      window.alert("Attachment size must not exceed 5 MB.");
      this.clearAdminReplyFile(input);
      return;
    }

    this.revokeAdminReplyPreviewUrl();
    this.adminReplyFile = file;
    this.adminReplyFilePreviewUrl = URL.createObjectURL(file);
  }

  previewAdminReplyFile(): void {
    if (!this.adminReplyFilePreviewUrl) {
      window.alert("Select an attachment first.");
      return;
    }

    window.open(
      this.adminReplyFilePreviewUrl,
      "_blank",
      "noopener,noreferrer",
    );
  }

  removeAdminReplyFile(): void {
    this.clearAdminReplyFile();
  }

  private clearAdminReplyFile(
    input?: HTMLInputElement,
  ): void {
    this.revokeAdminReplyPreviewUrl();
    this.adminReplyFile = null;

    if (input) {
      input.value = "";
    }
  }

  private revokeAdminReplyPreviewUrl(): void {
    if (this.adminReplyFilePreviewUrl) {
      URL.revokeObjectURL(this.adminReplyFilePreviewUrl);
      this.adminReplyFilePreviewUrl = null;
    }
  }

  sendAdminReply(): void {
    if (
      !this.selectedTicket ||
      ["Closed", "Deleted"].includes(
        this.selectedTicket.status,
      ) ||
      this.isSendingReply
    ) {
      return;
    }

    const message = this.adminReplyText.trim();

    if (!message && !this.adminReplyFile) {
      window.alert("Please enter a reply or select an attachment.");
      return;
    }

    const loggedInUser = this.userLoginService.User();
    const actionUser = String(loggedInUser?.userId || "").trim();

    if (!actionUser) {
      window.alert("Logged-in user information is unavailable.");
      return;
    }

    const ticket = this.selectedTicket;
    this.isSendingReply = true;

    const replyAttachmentFile =
      this.adminReplyFile;

    if (replyAttachmentFile) {
      this.isUploadingReplyAttachment = true;

      this.raiseTicketService
        .SupportTicket_UploadAttachment(
          ticket.ticketId,
          replyAttachmentFile,
          actionUser,
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (uploadResponse: any) => {
            this.isUploadingReplyAttachment = false;

            /*
             * UploadAttachment returns both:
             * 1. attachmentPath: relative database path
             * 2. attachmentUrl: complete downloadable URL
             *
             * Store the complete URL inside the activity message whenever it
             * is returned. This prevents the frontend from rebuilding the URL
             * with the API-gateway host (dev.apig.nishwik.com), while the
             * physical file is served by the master-service host
             * (dev.ms.nishwik.com).
             */
            const attachmentPath = String(
              uploadResponse?.attachmentPath ??
              uploadResponse?.AttachmentPath ??
              uploadResponse?.data?.attachmentPath ??
              uploadResponse?.data?.AttachmentPath ??
              uploadResponse?.attachmentUrl ??
              uploadResponse?.AttachmentUrl ??
              uploadResponse?.data?.attachmentUrl ??
              uploadResponse?.data?.AttachmentUrl ??
              "",
            ).trim();

            const attachmentName = String(
              uploadResponse?.originalFileName ??
              uploadResponse?.OriginalFileName ??
              uploadResponse?.data?.originalFileName ??
              uploadResponse?.data?.OriginalFileName ??
              uploadResponse?.storedFileName ??
              uploadResponse?.StoredFileName ??
              uploadResponse?.data?.storedFileName ??
              uploadResponse?.data?.StoredFileName ??
              uploadResponse?.fileName ??
              uploadResponse?.FileName ??
              uploadResponse?.data?.fileName ??
              uploadResponse?.data?.FileName ??
              replyAttachmentFile.name ??
              this.getAttachmentName(attachmentPath) ??
              "",
            ).trim();

            if (!attachmentPath) {
              this.isSendingReply = false;
              window.alert(
                "Attachment uploaded, but attachment path was not returned.",
              );
              return;
            }

            this.createAdminReplyActivity(
              ticket,
              message,
              actionUser,
              attachmentPath,
              attachmentName,
            );
          },
          error: (error: any) => {
            this.isUploadingReplyAttachment = false;
            this.isSendingReply = false;

            console.error("Admin reply attachment upload error:", error);

            window.alert(
              error?.error?.message ||
              error?.message ||
              "Unable to upload the attachment.",
            );
          },
        });

      return;
    }

    this.createAdminReplyActivity(
      ticket,
      message,
      actionUser,
      null,
      null,
    );
  }

  private createAdminReplyActivity(
    ticket: HospitalAdminTicket,
    message: string,
    actionUser: string,
    attachmentPath: string | null,
    attachmentName: string | null,
  ): void {
    const payload = {
      message,
      attachmentPath,
      attachmentName,
    };

    this.raiseTicketService
      .TicketActivity_Create({
        ticketId: ticket.ticketId,
        ticketComments:
          `${this.adminMessageJsonPrefix}${JSON.stringify(payload)}`,
        actionUser,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSendingReply = false;
          this.isUploadingReplyAttachment = false;
          this.adminReplyText = "";
          this.clearAdminReplyFile();

          this.publishSupportTicketMessageSignal({
            ticketId:
              ticket.ticketId,
            senderType:
              "ADMIN",
            createdOn:
              new Date().toISOString(),
          });

          this.loadTicketActivities(ticket);
          this.toastr.success(
            "Message sent successfully.",
            "Success",
          );
        },
        error: (error: any) => {
          this.isSendingReply = false;
          this.isUploadingReplyAttachment = false;

          console.error("Admin ticket reply error:", error);

          window.alert(
            error?.error?.message ||
            error?.message ||
            "Unable to send the reply.",
          );
        },
      });
  }

  viewActivityAttachment(
    activity: TicketActivity,
  ): void {
    if (
      !this.selectedTicket
    ) {
      window.alert(
        "Ticket information is not available.",
      );

      return;
    }

    const path =
      String(
        activity?.attachmentPath ??
        "",
      ).trim();

    if (!path) {
      window.alert(
        "Attachment is not available.",
      );

      return;
    }

    this.loadAttachmentByPath(
      this.selectedTicket.ticketId,
      path,
      activity.attachmentName ||
        this.getAttachmentName(
          path,
        ) ||
        "message-attachment",
      "view",
    );
  }

  viewTicketAttachment(
    ticket: HospitalAdminTicket,
    attachment: TicketAttachment,
  ): void {
    this.loadAttachmentByPath(
      ticket.ticketId,
      attachment.path,
      attachment.name,
      "view",
    );
  }

  downloadAttachment(
    ticket: HospitalAdminTicket,
    attachment: TicketAttachment,
  ): void {
    this.loadAttachmentByPath(
      ticket.ticketId,
      attachment.path,
      attachment.name,
      "download",
    );
  }

  /*
   * Every file is loaded through:
   *
   * GET /M/ST/ViewAttachment/{ticketId}?attachmentPath=<stored path>
   *
   * This allows file 1, file 2 and file 3 to retain their own paths.
   */
  private loadAttachmentByPath(
    ticketId: number,
    attachmentPath: string,
    fallbackFileName: string,
    action: "view" | "download",
  ): void {
    const normalizedAttachmentPath =
      this.normalizeAttachmentPathForApi(
        attachmentPath,
      );

    if (
      Number(ticketId) <= 0 ||
      !normalizedAttachmentPath ||
      this.isTicketAttachmentLoading
    ) {
      return;
    }

    let previewWindow:
      Window | null = null;

    if (action === "view") {
      previewWindow =
        window.open(
          "about:blank",
          "_blank",
        );

      if (!previewWindow) {
        this.toastr.warning(
          "Please allow pop-ups to view the attachment.",
          "Attachment",
        );

        return;
      }

      try {
        previewWindow.opener =
          null;

        previewWindow.document.title =
          "Loading attachment";

        previewWindow.document.body.innerHTML =
          "<p style='font-family:Arial,sans-serif;padding:20px'>Loading attachment...</p>";
      } catch {
        /*
         * The preview still works when the temporary page cannot be customized.
         */
      }
    }

    this.isTicketAttachmentLoading =
      true;

    this.ticketAttachmentAction =
      action;

    this.raiseTicketService
      .SupportTicket_ViewAttachment(
        ticketId,
        normalizedAttachmentPath,
      )
      .pipe(
        takeUntilDestroyed(
          this.destroyRef,
        ),
      )
      .subscribe({
        next: (
          response,
        ) => {
          this.isTicketAttachmentLoading =
            false;

          this.ticketAttachmentAction =
            null;

          const attachmentBlob =
            response.body;

          if (
            !attachmentBlob ||
            attachmentBlob.size <= 0
          ) {
            previewWindow?.close();

            this.toastr.error(
              "The attachment file is empty or unavailable.",
              "Attachment",
            );

            return;
          }

          const responseFileName =
            this.extractDownloadFileName(
              response.headers.get(
                "content-disposition",
              ),
            );

          const contentType =
            response.headers.get(
              "content-type",
            ) ||
            attachmentBlob.type ||
            "";

          const finalFileName =
            this.ensureAttachmentExtension(
              responseFileName ||
                fallbackFileName ||
                this.getAttachmentName(
                  normalizedAttachmentPath,
                ) ||
                `ticket-${ticketId}-attachment`,
              normalizedAttachmentPath,
              contentType,
            );

          const viewableBlob =
            this.createViewableAttachmentBlob(
              attachmentBlob,
              finalFileName,
              contentType,
            );

          const objectUrl =
            URL.createObjectURL(
              viewableBlob,
            );

          if (
            action === "view" &&
            previewWindow
          ) {
            previewWindow.location.href =
              objectUrl;

            window.setTimeout(
              () =>
                URL.revokeObjectURL(
                  objectUrl,
                ),
              60000,
            );

            return;
          }

          const downloadLink =
            document.createElement(
              "a",
            );

          downloadLink.href =
            objectUrl;

          downloadLink.download =
            finalFileName;

          downloadLink.style.display =
            "none";

          document.body.appendChild(
            downloadLink,
          );

          downloadLink.click();
          downloadLink.remove();

          window.setTimeout(
            () =>
              URL.revokeObjectURL(
                objectUrl,
              ),
            1000,
          );
        },

        error: (
          error: any,
        ) => {
          this.isTicketAttachmentLoading =
            false;

          this.ticketAttachmentAction =
            null;

          previewWindow?.close();

          console.error(
            "ViewAttachment API error:",
            error,
          );

          this.showAttachmentRequestError(
            error,
          );
        },
      });
  }

  private showAttachmentRequestError(
    error: any,
  ): void {
    const fallbackMessage =
      error?.status === 404
        ? "Attachment API route or attachment file was not found on the server."
        : "Unable to load the attachment.";

    const errorBody =
      error?.error;

    if (
      errorBody instanceof Blob
    ) {
      errorBody
        .text()
        .then(
          (
            text: string,
          ) => {
            let message =
              text?.trim() ||
              fallbackMessage;

            if (text) {
              try {
                const parsed =
                  JSON.parse(
                    text,
                  );

                message =
                  parsed?.message ||
                  parsed?.Message ||
                  parsed?.title ||
                  parsed?.Title ||
                  message;
              } catch {
                /*
                 * Keep the plain-text server response.
                 */
              }
            }

            this.toastr.error(
              message,
              "Attachment",
            );
          },
        )
        .catch(
          () => {
            this.toastr.error(
              fallbackMessage,
              "Attachment",
            );
          },
        );

      return;
    }

    this.toastr.error(
      errorBody?.message ||
      errorBody?.Message ||
      error?.message ||
      fallbackMessage,
      "Attachment",
    );
  }

  private createViewableAttachmentBlob(
    attachmentBlob: Blob,
    fileName: string,
    responseContentType: string,
  ): Blob {
    const normalizedContentType =
      String(
        responseContentType ||
        attachmentBlob.type ||
        "",
      )
        .split(";")[0]
        .trim()
        .toLowerCase();

    if (
      normalizedContentType &&
      normalizedContentType !==
        "application/octet-stream"
    ) {
      return attachmentBlob;
    }

    const normalizedFileName =
      String(
        fileName ||
        "",
      ).toLowerCase();

    let inferredContentType =
      "application/octet-stream";

    if (
      normalizedFileName.endsWith(
        ".pdf",
      )
    ) {
      inferredContentType =
        "application/pdf";
    } else if (
      normalizedFileName.endsWith(
        ".png",
      )
    ) {
      inferredContentType =
        "image/png";
    } else if (
      normalizedFileName.endsWith(
        ".jpg",
      ) ||
      normalizedFileName.endsWith(
        ".jpeg",
      )
    ) {
      inferredContentType =
        "image/jpeg";
    }

    return new Blob(
      [attachmentBlob],
      {
        type:
          inferredContentType,
      },
    );
  }

  private triggerDirectAttachmentDownload(
    attachmentUrl: string,
    fileName: string,
  ): void {
    const downloadLink =
      document.createElement(
        "a",
      );

    downloadLink.href =
      attachmentUrl;

    downloadLink.download =
      fileName;

    downloadLink.target =
      "_blank";

    downloadLink.rel =
      "noopener noreferrer";

    downloadLink.style.display =
      "none";

    document.body.appendChild(
      downloadLink,
    );

    downloadLink.click();
    downloadLink.remove();
  }

  private resolveAttachmentUrl(
    attachmentPath: string,
  ): string {
    const normalizedPath =
      String(
        attachmentPath ||
        "",
      )
        .trim()
        .replace(
          /\\/g,
          "/",
        );

    if (
      /^(https?:|blob:|data:)/i.test(
        normalizedPath,
      )
    ) {
      return normalizedPath;
    }

    try {
      /*
       * The POST upload request goes through the API gateway, but the response
       * confirms that the uploaded file is physically served by the master
       * service host. For example:
       *   API:  https://dev.apig.nishwik.com/M/ST/UploadAttachment
       *   File: https://dev.ms.nishwik.com/Uploads/SupportTickets/...
       *
       * Convert only the gateway host to the matching master-service host.
       * This also works for production-style hosts such as
       * apig.nishwik.com -> ms.nishwik.com.
       */
      const uploadApiUrl =
        new URL(
          SupportTicket_uploadAttachment,
          window.location.origin,
        );

      const fileServiceUrl =
        new URL(
          uploadApiUrl.origin,
        );

      fileServiceUrl.hostname =
        fileServiceUrl.hostname.replace(
          /(^|\.)apig\./i,
          "$1ms.",
        );

      const fileOrigin =
        fileServiceUrl.origin;

      const relativePath =
        normalizedPath
          .replace(
            /^\/+/,
            "",
          );

      if (
        relativePath
          .toLowerCase()
          .startsWith(
            "uploads/",
          )
      ) {
        return new URL(
          `/${relativePath}`,
          fileOrigin,
        ).toString();
      }

      if (
        relativePath.includes(
          "/",
        )
      ) {
        return new URL(
          `/${relativePath}`,
          fileOrigin,
        ).toString();
      }

      return new URL(
        `/Uploads/${relativePath}`,
        fileOrigin,
      ).toString();
    } catch (error) {
      console.error(
        "Unable to resolve ticket attachment URL:",
        error,
      );

      return normalizedPath;
    }
  }

  private extractDownloadFileName(
    contentDisposition:
      string |
      null,
  ): string | null {
    if (!contentDisposition) {
      return null;
    }

    const utf8FileNameMatch =
      contentDisposition.match(
        /filename\*\s*=\s*UTF-8''([^;]+)/i,
      );

    if (
      utf8FileNameMatch?.[1]
    ) {
      try {
        return decodeURIComponent(
          utf8FileNameMatch[1]
            .trim()
            .replace(
              /^"|"$/g,
              "",
            ),
        );
      } catch {
        return utf8FileNameMatch[1]
          .trim()
          .replace(
            /^"|"$/g,
            "",
          );
      }
    }

    const fileNameMatch =
      contentDisposition.match(
        /filename\s*=\s*"?([^";]+)"?/i,
      );

    return fileNameMatch?.[1]
      ? fileNameMatch[1].trim()
      : null;
  }

  private ensureAttachmentExtension(
    fileName: string,
    attachmentUrl: string,
    mimeType: string,
  ): string {
    const normalizedFileName =
      String(
        fileName ||
        "attachment",
      ).trim();

    if (
      /\.[a-z0-9]{2,8}$/i.test(
        normalizedFileName,
      )
    ) {
      return normalizedFileName;
    }

    const pathFileName =
      this.getAttachmentName(
        attachmentUrl
          .split("?")[0]
          .split("#")[0],
      );

    const pathExtension =
      pathFileName?.match(
        /\.[a-z0-9]{2,8}$/i,
      )?.[0];

    if (pathExtension) {
      return `${normalizedFileName}${pathExtension}`;
    }

    const extensionByMimeType:
      Record<
        string,
        string
      > = {
      "image/jpeg":
        ".jpg",
      "image/png":
        ".png",
      "application/pdf":
        ".pdf",
    };

    const mimeExtension =
      extensionByMimeType[
        String(
          mimeType ||
          "",
        )
          .split(";")[0]
          .trim()
          .toLowerCase()
      ];

    return mimeExtension
      ? `${normalizedFileName}${mimeExtension}`
      : normalizedFileName;
  }

  private buildTicketUpdatePayload(
    ticket: HospitalAdminTicket,
    status: TicketStatus,
    actionUser: string,
    assignedTo?: string | null,
  ): SupportTicketsUpdateRequestDTO {
    return {
      ticketId: ticket.ticketId,
      ticketNo: ticket.ticketNoForApi || undefined,
      moduleType:
        ticket.moduleType ||
        String(this.moduleType || "HOSPITAL").trim().toUpperCase(),
      moduleId: ticket.moduleId || Number(this.moduleId || 0),
      title: ticket.subject,
      tDesc: ticket.description,
      tType: this.optionalText(ticket.subCategory),
      category: this.optionalText(ticket.category),
      tagList: ticket.tagList || undefined,
      ticketOwner: ticket.ticketOwner,
      assignedTo:
        assignedTo ||
        ticket.assignedTo ||
        undefined,
      ticketStatus:
        this.toBackendTicketStatus(
          status,
        ),
      tPriority: ticket.priority || undefined,
      affectsCustomer: ticket.affectsCustomer || undefined,
      appVersion: ticket.appVersion || undefined,
      dueDate: ticket.dueDate || undefined,
      estimatedDuration: ticket.estimatedDuration || undefined,
      actualDuration: ticket.actualDuration || undefined,
      targetDate: ticket.targetDate || undefined,
      resolutionDate: ticket.resolutionDate || undefined,
      department: this.optionalText(ticket.department),
      raisedBy: this.optionalText(ticket.name),
      attachmentPath: ticket.attachmentPath || undefined,
      addField3: ticket.addField3 || undefined,
      addField4: ticket.addField4 || undefined,
      addField5: ticket.addField5 || undefined,
      otherDetail1: ticket.otherDetail1 || undefined,
      otherDetail2: ticket.otherDetail2 || undefined,
      otherDetail3: ticket.otherDetail3 || undefined,
      otherDetail4: ticket.otherDetail4 || undefined,
      otherDetail5: ticket.otherDetail5 || undefined,
      otherDetail6: ticket.otherDetail6 || undefined,
      actionUser,
    };
  }

  private toBackendTicketStatus(
    status: TicketStatus,
  ): string {
    switch (status) {
      case "In Progress":
        return "InProgress";

      case "Resolved":
        return "Resolved";

      case "Closed":
        return "Closed";

      case "Re-Open":
        return "ReOpen";

      case "Open":
      default:
        return "Open";
    }
  }

  updateTicketStatus(): void {
    if (
      !this.selectedTicket ||
      !this.updatedStatus ||
      this.isUpdatingStatus
    ) {
      return;
    }

    const loggedInUser =
      this.userLoginService.User();

    const actionUser =
      String(
        loggedInUser?.userId || "",
      ).trim();

    if (!actionUser) {
      window.alert(
        "Logged-in user information is unavailable.",
      );

      return;
    }

    const ticket =
      this.selectedTicket;

    const newStatus =
      this.updatedStatus;

    const updatePayload =
      this.buildTicketUpdatePayload(
        ticket,
        newStatus,
        actionUser,
      );



    this.isUpdatingStatus = true;

    this.raiseTicketService
      .SupportTickets_Update(
        updatePayload,
      )
      .pipe(
        takeUntilDestroyed(
          this.destroyRef,
        ),
      )
      .subscribe({
        next: (response: any) => {


          /*
           * Always create one activity entry so the
           * user can see the status change in the
           * ticket conversation/history.
           */
          const comment =
            this.statusComment.trim() ||
            `Ticket status updated to ${newStatus}.`;

          this.createStatusActivity(
            ticket.ticketId,
            comment,
            actionUser,
            newStatus,
          );
        },

        error: (error: any) => {
          console.error(
            "SupportTickets_Update error:",
            error,
          );

          this.isUpdatingStatus = false;

          window.alert(
            error?.error?.message ||
            error?.message ||
            "Unable to update ticket status.",
          );
        },
      });
  }

  private createStatusActivity(
    ticketId: number,
    comment: string,
    actionUser: string,
    updatedStatus: TicketStatus,
  ): void {
    const activityPayload:
      TicketActivityCreateRequestDTO = {
      ticketId,
      ticketComments:
        comment,
      actionUser,
    };



    this.raiseTicketService
      .TicketActivity_Create(
        activityPayload,
      )
      .pipe(
        takeUntilDestroyed(
          this.destroyRef,
        ),
      )
      .subscribe({
        next: (response: any) => {
          this.completeStatusUpdate(
            updatedStatus,
            true,
          );
        },

        error: (error: any) => {
          console.error(
            "TicketActivity_Create error:",
            error,
          );

          /*
           * The main ticket status has already been
           * updated successfully. Refresh the list
           * even when the activity entry fails.
           */
          this.completeStatusUpdate(
            updatedStatus,
            false,
          );
        },
      });
  }

  private completeStatusUpdate(
    updatedStatus: TicketStatus,
    activityCreated: boolean,
  ): void {
    this.isUpdatingStatus = false;

    if (activityCreated) {
      this.toastr.success(
        `Ticket status updated to ${updatedStatus}.`,
        "Success",
      );
    } else {
      this.toastr.warning(
        `Ticket status updated to ${updatedStatus}, but the status comment could not be saved.`,
        "Warning",
      );
    }

    window.dispatchEvent(
      new CustomEvent(
        "support-ticket-updated",
      ),
    );

    this.closeModal();
    this.getHospitalTickets();
  }

  private optionalText(
    value:
      string |
      null |
      undefined,
  ): string | undefined {
    const text =
      String(value || "").trim();

    if (
      !text ||
      text === "--"
    ) {
      return undefined;
    }

    return text;
  }

  closeModal(): void {
    this.selectedTicket = null;
    this.modalMode = null;

    this.selectedAssignee = "";
    this.assignmentDescription = "";

    this.updatedStatus = "";
    this.statusComment = "";
    this.isUpdatingStatus = false;
    this.isAssigningTicket = false;
    this.adminReplyText = "";
    this.isSendingReply = false;
    this.isUploadingReplyAttachment = false;
    this.isTicketAttachmentLoading = false;
    this.ticketAttachmentAction = null;
    this.clearAdminReplyFile();
  }

  getStatusClass(
    status: string,
  ): string {
    switch (status) {
      case "Open":
      case "Re-Open":
        return "ticket-status--open";

      case "In Progress":
        return "ticket-status--progress";

      case "Resolved":
        return "ticket-status--resolved";

      case "Closed":
      case "Deleted":
        return "ticket-status--closed";

      default:
        return "";
    }
  }

  getInitials(
    name: string,
  ): string {
    const parts =
      String(name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);

    if (parts.length === 0) {
      return "U";
    }

    if (parts.length === 1) {
      return parts[0]
        .charAt(0)
        .toUpperCase();
    }

    return (
      parts[0].charAt(0) +
      parts[
        parts.length - 1
      ].charAt(0)
    ).toUpperCase();
  }

  trackByTicketId(
    _index: number,
    ticket: HospitalAdminTicket,
  ): number {
    return ticket.ticketId;
  }

  @HostListener(
    "window:support-ticket-message-created",
    ["$event"],
  )
  onSupportTicketMessageCreated(
    event: CustomEvent,
  ): void {
    this.handleSupportTicketMessageSignal(
      event?.detail,
    );
  }

  @HostListener(
    "window:storage",
    ["$event"],
  )
  onSupportTicketMessageStorage(
    event: StorageEvent,
  ): void {
    if (!event.newValue) {
      return;
    }

    if (
      event.key ===
      this.ticketUpdateStorageKey
    ) {
      /*
       * A patient reopened a ticket in another same-origin tab/window.
       * Reload the current admin page so the ticket immediately appears
       * again in the Open queue.
       */
      this.getHospitalTickets();
      return;
    }

    if (
      event.key !==
      this.messageStorageKey
    ) {
      return;
    }

    try {
      this.handleSupportTicketMessageSignal(
        JSON.parse(
          event.newValue,
        ),
      );
    } catch (error) {
      console.error(
        "Unable to parse support-ticket message signal:",
        error,
      );
    }
  }

  @HostListener("window:support-ticket-created")
  onSupportTicketCreated(): void {
    this.getHospitalTickets();
  }

  @HostListener(
    "window:support-ticket-updated",
    ["$event"],
  )
  onSupportTicketUpdated(
    event: CustomEvent,
  ): void {
    const ticketId =
      Number(
        event?.detail?.ticketId ??
        event?.detail?.TicketId ??
        0,
      );

    const status =
      String(
        event?.detail?.status ??
        event?.detail?.Status ??
        "",
      )
        .trim()
        .toLowerCase()
        .replace(
          /[_-]+/g,
          " ",
        );

    if (
      ticketId > 0 &&
      [
        "reopen",
        "re open",
        "reopened",
        "re opened",
      ].includes(status)
    ) {
      const ticket =
        this.tickets.find(
          (
            item:
              HospitalAdminTicket,
          ) =>
            item.ticketId ===
            ticketId,
        );

      if (ticket) {
        /*
         * Only change the status. Do not clear AssignedTo.
         */
        ticket.status =
          "Re-Open";
      }
    }

    this.getHospitalTickets();
  }

  @HostListener(
    "window:focus",
  )
  onWindowFocus(): void {
    if (
      this.refreshTicketsOnWindowFocus
    ) {
      this.getHospitalTickets();
    }
  }

  @HostListener(
    "document:visibilitychange",
  )
  onVisibilityChange(): void {
    if (
      this.refreshTicketsOnWindowFocus &&
      document.visibilityState ===
      "visible"
    ) {
      this.getHospitalTickets();
    }
  }

  @HostListener(
    "document:click",
  )
  closeActionMenu(): void {
    this.openActionMenuId = null;
  }
}